/** Existing Cockpit beat while a job is running or a draft is waiting. */
export const MAIN_COMMAND_FAST_POLL_MS = 10_000;
/** Visible idle desk. The room refresh already uses this same beat. */
export const MAIN_COMMAND_IDLE_POLL_MS = 30_000;
/** Existing Worker-terminal row read while that row is active. */
export const WORKER_TERMINAL_FAST_POLL_MS = 5_000;
/** Visible idle Worker-terminal row read. */
export const WORKER_TERMINAL_IDLE_POLL_MS = 30_000;

const JOB_RUNNING_PLAN = new Set(['sent', 'evaluating']);
const DRAFT_WAITING_PLAN = new Set(['drafting', 'proposed']);
const ACTIVE_TERMINAL = new Set(['starting', 'running', 'waiting_for_input', 'waiting_for_approval']);

export function mainCommandPollActive(input: {
  readonly runningCount: number;
  readonly commandPlanStatuses: readonly string[];
  readonly planStatuses: readonly string[];
}): { readonly jobRunning: boolean; readonly draftWaiting: boolean } {
  const jobRunning = input.runningCount > 0
    || input.commandPlanStatuses.some((status) => JOB_RUNNING_PLAN.has(status));
  const draftWaiting = input.planStatuses.some((status) => status === 'proposed')
    || input.commandPlanStatuses.some((status) => DRAFT_WAITING_PLAN.has(status));
  return { jobRunning, draftWaiting };
}

/** Null while the tab is hidden. Fast while a job or draft is in hand. Otherwise the idle floor. */
export function mainCommandPollIntervalMs(input: {
  readonly visibilityState: string;
  readonly jobRunning: boolean;
  readonly draftWaiting: boolean;
}): number | null {
  if (input.visibilityState === 'hidden') return null;
  if (input.jobRunning || input.draftWaiting) return MAIN_COMMAND_FAST_POLL_MS;
  return MAIN_COMMAND_IDLE_POLL_MS;
}

/** A Worker row that is starting, running, waiting, or already carrying a task. */
export function workerTerminalFeedHot(terminalStatus: string | null, taskTitle: string | null): boolean {
  if (taskTitle !== null && taskTitle !== '') return true;
  return terminalStatus !== null && ACTIVE_TERMINAL.has(terminalStatus);
}

/** Null while the surface or the tab is hidden. */
export function workerTerminalPollMs(input: {
  readonly surfaceVisible: boolean;
  readonly visibilityState: string;
  readonly active: boolean;
}): number | null {
  if (!input.surfaceVisible || input.visibilityState === 'hidden') return null;
  return input.active ? WORKER_TERMINAL_FAST_POLL_MS : WORKER_TERMINAL_IDLE_POLL_MS;
}

export interface VisibleDelayHost {
  visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

/**
 * Wait `delayMs`, but only while the tab is visible. A hidden tab suspends the
 * remainder and continues it when the tab is shown again. Nothing is read while hidden.
 */
export function delayWhileVisible(
  delayMs: number,
  host: VisibleDelayHost = document,
): Promise<void> {
  return new Promise((resolve) => {
    let remaining = delayMs;
    let startedAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const clear = () => {
      if (timer === undefined) return;
      clearTimeout(timer);
      timer = undefined;
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      clear();
      host.removeEventListener('visibilitychange', onVisibility);
      resolve();
    };
    const arm = () => {
      if (host.visibilityState === 'hidden' || timer !== undefined) return;
      startedAt = Date.now();
      timer = setTimeout(finish, remaining);
    };
    const onVisibility = () => {
      if (host.visibilityState === 'hidden') {
        if (timer !== undefined) {
          remaining = Math.max(0, remaining - (Date.now() - startedAt));
          clear();
        }
        return;
      }
      arm();
    };
    host.addEventListener('visibilitychange', onVisibility);
    arm();
  });
}
